import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import PurchaseFormContext from './PurchaseFormContext';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import { getValidationFunctions } from '../../../tools/validations/formValidation';
import componentMap from '../../Steps/componentMap';

export default function PurchaseFormProvider({ children }) {
  const { checkoutPageState } = useContext(CheckoutPageContext) || {};
  const {
    orderContact, checkoutFormConfig, userData, shippingAddress, shippingAddressConfig,
  } = checkoutPageState || {};

  const validationFunctions = useMemo(() => {
    const validationConfig = {
      invalidEmailMessage: orderContact?.invalidEmailLabel?.value,
      invalidPhoneMessage: orderContact?.invalidPhoneLabel?.value,
    };
    return getValidationFunctions(validationConfig);
  }, [orderContact]);

  const defaultValues = useMemo(() => {
    const allFields = Object.values(componentMap).reduce((acc, Component) => {
      if (Component && typeof Component.validationFields === 'function') {
        return { ...acc, ...Component.validationFields(checkoutPageState) };
      }
      return acc;
    }, {});

    return {
      ...allFields,
      smsOptIn: false,
      legalTermsChecked: false,
      legalTermsAgeChecked: false,
    };
  }, [checkoutPageState]);

  const methods = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  const {
    getValues, setValue, getFieldState, watch, reset,
  } = methods;

  useEffect(() => {
    if (!orderContact) return;

    const prev = getValues();

    const guestCustomerMailId = sessionStorage?.getItem('guestCustomerMailId');
    const emailFromContext = (guestCustomerMailId && !userData?.isLoggedIn)
      ? guestCustomerMailId
      : (orderContact.email || '');
    const phoneFromContext = orderContact.phoneNumber || '';

    const initIfEmpty = (path, value) => {
      const currentVal = getValues(path);
      const isUnset = currentVal === undefined
        || currentVal === null
        || (typeof currentVal === 'string' && currentVal.trim() === '');
      if (isUnset) setValue(path, value, { shouldDirty: false, shouldTouch: false });
    };

    // Root fields: never overwrite non-empty
    initIfEmpty('email', emailFromContext);
    initIfEmpty('phone', phoneFromContext);

    // Address-specific: prefer existing root values, else context
    const addrType = orderContact.addressType;
    if (addrType) {
      const sourceEmail = (prev?.email && String(prev.email).trim() !== '') ? prev.email : emailFromContext;
      const sourcePhone = (prev?.phone && String(prev.phone).trim() !== '') ? prev.phone : phoneFromContext;
      initIfEmpty(`addresses.${addrType}.email`, sourceEmail || '');
      initIfEmpty(`addresses.${addrType}.phone`, sourcePhone || '');
    }

    // Billing fallback: do not overwrite
    const sourceEmailForBilling = (prev?.email && String(prev.email).trim() !== '') ? prev.email : emailFromContext;
    const sourcePhoneForBilling = (prev?.phone && String(prev.phone).trim() !== '') ? prev.phone : phoneFromContext;
    initIfEmpty('addresses.billing.email', sourceEmailForBilling || '');
    initIfEmpty('addresses.billing.phone', sourcePhoneForBilling || '');

    // smsOptIn: do not sync from context; default to false and
    // preserve user changes

    // Checkout form config defaults
    if (
      checkoutFormConfig?.defaultValues
      && typeof checkoutFormConfig.defaultValues === 'object'
    ) {
      Object.entries(checkoutFormConfig.defaultValues).forEach(([path, value]) => {
        initIfEmpty(path, value);
      });
    }
  }, [orderContact, checkoutFormConfig, getValues, setValue, getFieldState, userData]);

  // No special watcher needed for smsOptIn; user input is preserved by default

  // Keep address-specific email/phone in sync with root when user updates them,
  // but only if the address fields themselves haven't been edited (not dirty).
  useEffect(() => {
    const subscription = watch?.((value, { name }) => {
      if (name !== 'email' && name !== 'phone') return;
      const newVal = value?.[name] ?? '';
      const addrType = checkoutPageState?.orderContact?.addressType;
      const targets = [];
      if (addrType) {
        targets.push(`addresses.${addrType}.${name === 'email' ? 'email' : 'phone'}`);
      }
      // Always consider billing as a mirror unless user edited it
      targets.push(`addresses.billing.${name === 'email' ? 'email' : 'phone'}`);

      targets.forEach((path) => {
        const fieldState = getFieldState ? getFieldState(path) : { isDirty: false };
        if (!fieldState.isDirty) {
          const currentTargetVal = getValues(path);
          if (currentTargetVal !== newVal) {
            setValue(path, newVal, { shouldDirty: false, shouldTouch: false });
          }
        }
      });
    });
    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') subscription.unsubscribe();
    };
  }, [watch, getFieldState, getValues, setValue, checkoutPageState?.orderContact?.addressType]);

  // When addressType switches to pickup (popins), force smsOptIn to false
  // while hidden, but remember the user's previous choice and restore it
  // when returning to homeDelivery.
  useEffect(() => {
    const addrType = checkoutPageState?.orderContact?.addressType;
    if (!addrType) return;

    const prevType = getValues('__prevAddressType');
    const setHidden = (path, val) => setValue(
      path,
      val,
      { shouldDirty: false, shouldTouch: false },
    );

    // Entering pickup (popins)
    if (addrType === 'popins' && prevType !== 'popins') {
      const currentOptIn = getValues('smsOptIn');
      setHidden('__smsOptInSaved', typeof currentOptIn === 'boolean' ? currentOptIn : false);
      if (currentOptIn) setHidden('smsOptIn', false);
    }

    // Leaving pickup back to homeDelivery
    if (prevType === 'popins' && addrType === 'homeDelivery') {
      const saved = getValues('__smsOptInSaved');
      if (typeof saved === 'boolean') setHidden('smsOptIn', saved);
    }

    // Track prev
    setHidden('__prevAddressType', addrType);
  }, [checkoutPageState?.orderContact?.addressType, getValues, setValue]);

  const stateField = useMemo(
    () => shippingAddressConfig?.addressFields?.find((item) => item.name === 'addresses.homeDelivery.state'),
    [shippingAddressConfig],
  );

  useEffect(() => {
    if (shippingAddress) {
      const selectedStateOption = stateField?.options?.find(
        (option) => option.value === shippingAddress.state,
      );
      reset((prev) => ({
        ...prev,
        addresses: {
          ...prev.addresses,
          homeDelivery: {
            ...prev.addresses?.homeDelivery,
            ...shippingAddress,
            state: selectedStateOption ? shippingAddress.state : '',
          },
        },
      }));
    }
  }, [shippingAddress, reset, stateField]);

  const contextValue = useMemo(() => ({
    methods,
    validationFunctions,
  }), [methods, validationFunctions]);

  return (
    <PurchaseFormContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        {children}
      </FormProvider>
    </PurchaseFormContext.Provider>
  );
}

PurchaseFormProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
